import { useQuery } from "@apollo/client";
import EmptyState from "components/core/EmptyState";
import UserAvatar from "components/User/UserAvatar";
import { LiquidPill, resolveCorporateValueVisual } from "components/ui/v2";
import { CorporateValueRankingModalV2 } from "components/ui/v2/corporate-value/CorporateValueRankingModalV2";
import {
  GET_RANKING_CORPORATE_VALUES,
  ICorporateValueRanking,
  ICorporateValueRankingByVendor
} from "gql/CorporateValues/queries";
import { Award } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EModalExploreType } from "types/Explorer.types";
import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";
import { UserItemRanking } from "../SideCard/UserItemRanking";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

export interface CoporateValueByVendor {
  data: ICorporateValueRankingByVendor | undefined;
}

interface RecognitionCorporateValueRankingWidgetProps {
  readonly variant?: "default" | "v2";
}

const shuffleRanking = (ranking: ICorporateValueRanking[]) => {
  const shuffled = [...ranking];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index]
    ];
  }

  return shuffled;
};

const RecognitionCorporateValueRankingWidget = (
  props: RecognitionCorporateValueRankingWidgetProps
) => {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const [rankingOpen, setRankingOpen] = useState(false);
  const { corporateRanking, setCorporateRanking, hasHydrated } =
    useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(corporateRanking.lastFetched);

  const { data: queriedCorporateRanking, loading: rankingLoading } =
    useQuery<ICorporateValueRankingByVendor>(GET_RANKING_CORPORATE_VALUES, {
      fetchPolicy: "network-only",
      skip: !hasHydrated || hasValidCache,
      onCompleted: (data) => {
        setCorporateRanking(data);
      }
    });

  const dataCorporateRanking = hasValidCache
    ? (corporateRanking.data as ICorporateValueRankingByVendor | null)
    : queriedCorporateRanking ||
      (corporateRanking.data as ICorporateValueRankingByVendor | null);

  const corporateValueRankingByVendor =
    dataCorporateRanking?.corporateValueRankingByVendor;

  const [activeIndex, setActiveIndex] = useState(0);

  const v2Ranking = useMemo(
    () => shuffleRanking((corporateValueRankingByVendor ?? []).slice(0, 10)),
    [corporateValueRankingByVendor]
  );

  useEffect(() => {
    if (activeIndex > Math.max(v2Ranking.length - 1, 0)) {
      setActiveIndex(0);
    }
  }, [activeIndex, v2Ranking.length]);

  const activeRankingItem = v2Ranking[activeIndex];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev >= v2Ranking.length - 1 ? 0 : prev + 1));
  }, [v2Ranking.length]);

  useEffect(() => {
    if (variant !== "v2" || v2Ranking.length <= 1) return;

    const interval = window.setInterval(() => {
      goNext();
    }, 4200);

    return () => window.clearInterval(interval);
  }, [goNext, v2Ranking.length, variant]);

  const mostRepeatedValue = useMemo(() => {
    const recognitions = activeRankingItem?.recognitionResume ?? [];
    if (!recognitions.length) return null;

    return [...recognitions].sort(
      (a, b) => (b.quantityReceived ?? 0) - (a.quantityReceived ?? 0)
    )[0];
  }, [activeRankingItem?.recognitionResume]);

  const activeVisual = resolveCorporateValueVisual(
    mostRepeatedValue?.corporateValueTitle ?? "",
    activeIndex
  );

  const renderItems = (recognitions: ICorporateValueRanking[]) =>
    recognitions.map((r) => (
      <div key={`${r.user?.id}_recognition_card`}>
        <UserItemRanking
          data={r.user}
          quantity={r.recognitions}
          type={EModalExploreType.RECOGNITION}
          compact={variant === "v2"}
        />
      </div>
    ));

  const handleViewMore = () => {
    setRankingOpen(true);
  };

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  const initialLoading = rankingLoading && !dataCorporateRanking;

  if (initialLoading) {
    return (
      <ActiveSideCard
        title={t("Clevers mejor valorados")}
        loading
        variant={variant}
        collapsible={false}
        defaultExpanded
        icon={
          variant === "v2" ? (
            <Award size={16} strokeWidth={1} color="#0073FF" />
          ) : null
        }
      >
        <span />
      </ActiveSideCard>
    );
  }

  return (
    <>
      <style jsx global>{`
        @keyframes explore-ranking-person-enter {
          0% {
            opacity: 0;
            transform: translateY(14px) scale(0.94);
            filter: blur(8px);
          }
          58% {
            opacity: 1;
            transform: translateY(-2px) scale(1.018);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes explore-ranking-avatar-pop {
          0% {
            opacity: 0;
            transform: scale(0.72) rotate(-4deg);
          }
          68% {
            opacity: 1;
            transform: scale(1.07) rotate(1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes explore-ranking-copy-rise {
          0% {
            opacity: 0;
            transform: translateY(7px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .explore-ranking-person-enter {
          animation: explore-ranking-person-enter 560ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity, filter;
        }
        .explore-ranking-person-enter .explore-ranking-avatar-pop {
          animation: explore-ranking-avatar-pop 620ms
            cubic-bezier(0.34, 1.56, 0.64, 1) 70ms both;
        }
        .explore-ranking-person-enter .explore-ranking-copy-rise {
          animation: explore-ranking-copy-rise 420ms
            cubic-bezier(0.22, 1, 0.36, 1) 150ms both;
        }
      `}</style>
      {(dataCorporateRanking?.corporateValueRankingByVendor?.length ?? 0) >
        0 && (
        <ActiveSideCard
          title={t("Clevers mejor valorados")}
          variant={variant}
          collapsible={false}
          buttonText={
            (corporateValueRankingByVendor?.length ?? 0) > 10
              ? t("Ver todos")
              : ""
          }
          onViewMore={handleViewMore}
          defaultExpanded
          icon={
            variant === "v2" ? (
              <Award size={16} strokeWidth={1} color="#0073FF" />
            ) : null
          }
        >
          {corporateValueRankingByVendor?.length === 0 && (
            <EmptyState
              title={t("Aun no hay reconocimientos")}
              text={t("Acá podrás ver los reconocimientos otorgados")}
            />
          )}

          {(corporateValueRankingByVendor?.length ?? 0) && (
            <div>
              <div>
                {corporateValueRankingByVendor?.length &&
                  variant === "default" &&
                  renderItems(corporateValueRankingByVendor.slice(0, 3))}
                {corporateValueRankingByVendor?.length && variant === "v2" && (
                  <div className="space-y-2.5">
                    {activeRankingItem?.user && (
                      <div
                        key={activeRankingItem.user.id}
                        className="explore-ranking-person-enter relative px-3 py-2"
                      >
                        <div className="relative flex flex-col items-center text-center">
                          <div className="explore-ranking-avatar-pop rounded-full ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 dark:ring-zinc-900 dark:ring-offset-zinc-800">
                            <UserAvatar
                              user={activeRankingItem.user}
                              size={68}
                            />
                          </div>

                          <p className="explore-ranking-copy-rise mt-2 max-w-full break-words px-2 text-[14px] font-semibold leading-tight text-slate-900 dark:text-zinc-100">
                            {[
                              activeRankingItem.user?.name,
                              activeRankingItem.user?.lastname
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>

                          <LiquidPill
                            variant={activeVisual.variant}
                            appearance="tinted"
                            size="xs"
                            text={
                              mostRepeatedValue?.corporateValueTitle ??
                              t("Sin valor")
                            }
                            className="explore-ranking-copy-rise mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </ActiveSideCard>
      )}
      <CorporateValueRankingModalV2
        open={rankingOpen}
        onClose={() => setRankingOpen(false)}
        data={dataCorporateRanking?.corporateValueRankingByVendor ?? []}
      />
    </>
  );
};

export default RecognitionCorporateValueRankingWidget;
